import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Chip, Divider, Stack } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import type { ICategoriaProduto } from "interfaces/loja";
import {
  CATEGORIAS_ACCENT,
  detailPanelSx,
  emptyStateSx,
  saveButtonSx,
} from "../categorias.theme";

type Props = {
  categoria: ICategoriaProduto | null;
  parentNome: string | null;
  onAddSub: (cat: ICategoriaProduto) => void;
  onEdit: (cat: ICategoriaProduto) => void;
  onDelete: (cat: ICategoriaProduto) => void;
};

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
        {label}
      </MDTypography>
      <MDTypography variant="body2" sx={{ mt: 0.25 }}>
        {value}
      </MDTypography>
    </Box>
  );
}

export default function CategoriaDetailPanel({
  categoria,
  parentNome,
  onAddSub,
  onEdit,
  onDelete,
}: Props) {
  if (!categoria) {
    return (
      <MDBox sx={{ ...emptyStateSx, py: 8, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <AccountTreeOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
        <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
          Selecione uma categoria
        </MDTypography>
        <MDTypography variant="body2" color="text.secondary">
          Clique em um item da árvore para ver detalhes e ações.
        </MDTypography>
      </MDBox>
    );
  }

  const subCount = categoria.subcategorias?.length ?? 0;

  return (
    <MDBox sx={detailPanelSx}>
      <MDBox
        px={2.5}
        py={2}
        sx={{
          bgcolor: "grey.50",
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <MDTypography variant="overline" color="text.secondary" letterSpacing={1}>
          Detalhes
        </MDTypography>
        <MDTypography variant="h5" fontWeight="bold" color={CATEGORIAS_ACCENT}>
          {categoria.nome}
        </MDTypography>
        {subCount > 0 ? (
          <Chip
            size="small"
            label={`${subCount} subcategoria${subCount === 1 ? "" : "s"}`}
            sx={{
              mt: 1,
              bgcolor: "rgba(249, 96, 48, 0.12)",
              color: CATEGORIAS_ACCENT,
              fontWeight: 600,
            }}
          />
        ) : null}
      </MDBox>

      <MDBox p={2.5}>
        {categoria.imagem ? (
          <MDBox
            component="img"
            src={categoria.imagem}
            alt={categoria.nome}
            sx={{
              width: "100%",
              maxHeight: 180,
              objectFit: "cover",
              borderRadius: 2,
              mb: 2,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          />
        ) : null}

        <Stack spacing={2}>
          <InfoRow label="Slug" value={categoria.slug} />
          {categoria.descricao ? (
            <InfoRow label="Descrição" value={categoria.descricao} />
          ) : null}
          {parentNome ? <InfoRow label="Categoria pai" value={parentNome} /> : null}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Stack direction="row" flexWrap="wrap" gap={1}>
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            startIcon={<AddOutlinedIcon />}
            onClick={() => onAddSub(categoria)}
            sx={saveButtonSx}
          >
            Subcategoria
          </MDButton>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => onEdit(categoria)}
          >
            Editar
          </MDButton>
          <MDButton
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => onDelete(categoria)}
          >
            Excluir
          </MDButton>
        </Stack>
      </MDBox>
    </MDBox>
  );
}
